const emptyMatchKeyRow = {
  creationScore: "",
  id: 1,
  keyDefinition: "",
  score: "",
};

function parseMatchKey(matchKey, index) {
  const parts = (matchKey || "").split(":");
  return {
    creationScore: parts.length > 3 ? parts[3] : "",
    id: index + 1,
    keyDefinition: parts[1] || "",
    score: parts[2] || "",
    sortableId: `match-key-row-${index + 1}`,
  };
}

function parseMatchKeys(matchKeys) {
  return (matchKeys || []).map(parseMatchKey);
}

function resequenceMatchKeyRows(rows) {
  return rows.map((row, index) => ({
    ...row,
    id: index + 1,
  }));
}

function createMatchKeyRow(rows) {
  const nextSortableId =
    Math.max(
      0,
      ...rows.map((row) => {
        const match = `${row.sortableId || ""}`.match(
          /^match-key-row-(\d+)$/
        );
        return match ? Number(match[1]) : 0;
      })
    ) + 1;

  return {
    ...emptyMatchKeyRow,
    id: rows.length + 1,
    sortableId: `match-key-row-${nextSortableId}`,
  };
}

function moveMatchKeyRow(rows, index, direction) {
  const targetIndex = index + direction;
  if (targetIndex < 0 || targetIndex >= rows.length) {
    return rows;
  }

  const updatedRows = [...rows];
  const [row] = updatedRows.splice(index, 1);
  updatedRows.splice(targetIndex, 0, row);

  return resequenceMatchKeyRows(updatedRows);
}

function getMatchKeyRowSortableId(row) {
  return row.sortableId || `match-key-row-${row.id}`;
}

function reorderMatchKeyRows(rows, activeId, overId) {
  if (!activeId || !overId || activeId === overId) {
    return rows;
  }

  const oldIndex = rows.findIndex(
    (row) => getMatchKeyRowSortableId(row) === activeId
  );
  const newIndex = rows.findIndex(
    (row) => getMatchKeyRowSortableId(row) === overId
  );

  if (oldIndex < 0 || newIndex < 0) {
    return rows;
  }

  const updatedRows = [...rows];
  const [row] = updatedRows.splice(oldIndex, 1);
  updatedRows.splice(newIndex, 0, row);

  return resequenceMatchKeyRows(updatedRows);
}

function serializeMatchKeys(rows) {
  return resequenceMatchKeyRows(rows).map((row) => {
    const serialized = [
      row.id,
      row.keyDefinition.trim(),
      row.score,
    ];

    if (`${row.creationScore}`.trim() !== "") {
      serialized.push(row.creationScore);
    }

    return serialized.join(":");
  });
}

function validateMatchKeyRows(rows) {
  const errors = [];

  rows.forEach((row, index) => {
    const rowNumber = index + 1;
    if (!row.keyDefinition || !row.keyDefinition.trim()) {
      errors.push(`Match key ${rowNumber} requires a key definition.`);
    }

    if (
      `${row.score}`.trim() === "" ||
      !Number.isInteger(Number(row.score))
    ) {
      errors.push(`Match key ${rowNumber} requires a whole-number score.`);
    }

    if (
      `${row.creationScore}`.trim() !== "" &&
      !Number.isInteger(Number(row.creationScore))
    ) {
      errors.push(
        `Match key ${rowNumber} requires a whole-number creation score.`
      );
    }
  });

  return errors;
}

function normalizeWarehouseSettings(settings, organisationFallback) {
  return {
    AzureConnectionString: {
      HasValue: !!settings?.AzureConnectionString?.HasValue,
    },
    InstallationId: settings?.InstallationId || "",
    MatchKeys: settings?.MatchKeys || [],
    MatchThreshold:
      settings?.MatchThreshold === undefined
        ? ""
        : settings.MatchThreshold,
    SynchronisationToCRMBatchSize:
      settings?.SynchronisationToCRMBatchSize === undefined
        ? ""
        : settings.SynchronisationToCRMBatchSize,
    SynchronisationFromCRMBatchSize:
      settings?.SynchronisationFromCRMBatchSize === undefined
        ? ""
        : settings.SynchronisationFromCRMBatchSize,
    SynchronisationMergeBatchSize:
      settings?.SynchronisationMergeBatchSize === undefined
        ? ""
        : settings.SynchronisationMergeBatchSize,
    OrganisationName:
      settings?.OrganisationName || organisationFallback || "",
    QueueName: settings?.QueueName || "",
  };
}

function buildWarehouseSettingsPayload(values, matchKeyRows) {
  return {
    OrganisationName: values.OrganisationName,
    MatchThreshold: Number(values.MatchThreshold),
    SynchronisationToCRMBatchSize: Number(values.SynchronisationToCRMBatchSize),
    SynchronisationFromCRMBatchSize: Number(values.SynchronisationFromCRMBatchSize),
    SynchronisationMergeBatchSize: Number(values.SynchronisationMergeBatchSize),
    MatchKeys: serializeMatchKeys(matchKeyRows),
    AzureConnectionString: values.AzureConnectionString || "",
    QueueName: values.QueueName,
  };
}

function matchKeysHaveChanged(initialRows, currentRows) {
  return (
    JSON.stringify(serializeMatchKeys(initialRows)) !==
    JSON.stringify(serializeMatchKeys(currentRows))
  );
}

export {
  buildWarehouseSettingsPayload,
  createMatchKeyRow,
  getMatchKeyRowSortableId,
  matchKeysHaveChanged,
  moveMatchKeyRow,
  normalizeWarehouseSettings,
  parseMatchKeys,
  reorderMatchKeyRows,
  resequenceMatchKeyRows,
  serializeMatchKeys,
  validateMatchKeyRows,
};
